import React, { useState, useEffect } from "react";
import cookie from "react-cookies";
import queryString from "query-string";
import {
  Button,
  Rating,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MainLayout from "layout/MainLayout";
import Review from "components/Review";
import axios from "utils/axios";
import { getFormattedAddress, getPhoneNumberMask } from "utils/utils";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import { toast } from "react-toastify";

const ResourceView = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [id, setId] = useState<number>(0);
  const [favourite, setFavourite] = useState<boolean>(false);
  const [resource, setResource] = useState<any>({});
  const [reviews, setReviews] = useState<Array<any>>([]);
  const [reviewContent, setReviewContent] = useState<string>("");
  const [reviewRating, setReviewRating] = useState<number>(0);
  const [rating, setRating] = useState<number>(0);

  useEffect(() => {
    const queryParams = queryString.parse(window.location.search);
    setId((queryParams.id || 0) as number);
  }, []); //eslint-disable-line
  useEffect(() => {
    if (id !== 0) {
      loadResource();
      getRating();
      getFavourite();
      loadReviews();
      addClickCount();
    }
  }, [id]); //eslint-disable-line
  const getRating = async () => {
    const result = await axios.get(`/api/review/getRatingByResourceId/${id}`);
    setRating(result.data.rating);
  };

  const getFavourite = async () => {
    const { tokenId } = cookie.load("user");
    const result = await axios.post("/api/favourite/getFavourite", {
      tokenId,
      resourceId: id,
    });
    setFavourite(result.data.favourite);
  };

  const loadResource = async () => {
    const result = await axios.get(`/api/resource/find/${id}`);
    setResource(result.data);
  };

  const loadReviews = async () => {
    const { data } = await axios.get(`/api/review/findByResourceId/${id}`);
    setReviews(data);
  };

  const addClickCount = async () => {
    await axios.get(`/api/resource/addCount/${id}`);
  };

  const handleFavourite = async () => {
    const { tokenId, title: username } = cookie.load("user");
    try {
      await axios.post("/api/favourite/addFavourite", {
        tokenId,
        resourceId: id,
        favourite: !favourite,
      });
      await axios.post("/api/activity/create", {
        tokenId,
        username,
        resourceOrg: resource.organization,
        resourceName: resource.name,
        resourceType: resource.type,
        resourceId: resource.id,
        activityType: `${favourite === true ? "Unfavorited" : "Favorited"}`,
      });
      setFavourite((prev) => !prev);
    } catch (error: any) {
      toast.error(error.message);
    }
  };
  const handleOpen = () => {
    setReviewRating(0);
    setReviewContent("");
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleSubmit = async () => {
    if (reviewRating === null || reviewRating === 0 || reviewContent === "") {
      toast.warning("Please fill all fields");
      return;
    }

    const { tokenId, title: username, media: imageUrl } = cookie.load("user");

    try {
      const result = await axios.post("/api/review/create", {
        tokenId,
        username,
        organization: resource.name,
        resourceId: id,
        imageUrl,
        rating: reviewRating,
        content: reviewContent,
      });
      toast.success(result.data.message);
      loadReviews();
      getRating();
      await axios.post("/api/activity/create", {
        tokenId,
        username,
        resourceOrg: resource.organization,
        resourceName: resource.name,
        resourceType: resource.type,
        resourceId: resource.id,
        activityType: "Reviewed",
        rating: reviewRating,
      });
      handleClose();
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };
  return (
    <MainLayout title={resource.name}>
      <div className="containers my-12">
        <div className="flex gap-8 sm:flex-col">
          <div className="flex flex-col flex-grow justify-center">
            <div className="relative grid gap-[12px] border-solid border-[1px] border-[#bdbdbd] p-[12px] rounded-[4px] shadow-md">
              <h1>{resource.name}</h1>
              <div
                onClick={handleFavourite}
                className="absolute top-0 right-[4px] cursor-pointer"
              >
                {!favourite ? (
                  <FavoriteBorderIcon style={{ color: "black" }} />
                ) : (
                  <FavoriteIcon style={{ color: "red" }} />
                )}
              </div>
              <div className="font-mont text-xl leading-8 text-[#4C4C4C] flex flex-col">
                <Rating value={rating} readOnly />
                <div className="flex items-center gap-[8px]">
                  <LocationOnIcon />
                  {getFormattedAddress(resource.address)}
                </div>
                <a
                  className="flex items-center gap-[8px]"
                  href={`tel:${resource.phoneNumber}`}
                >
                  <PhoneInTalkIcon />
                  {getPhoneNumberMask(resource.phoneNumber)}
                </a>
                <a
                  href={`mailto:${resource.email}`}
                  className="flex items-center gap-[8px]"
                >
                  {resource.email && <ForwardToInboxIcon />}
                  {resource.email}
                </a>
                <div
                  className="flex items-center gap-[8px] cursor-pointer"
                  onClick={() => window.open(resource.websiteLink, "_blank")}
                >
                  {resource.websiteLink && <TravelExploreIcon />}
                  {resource.websiteLink}
                </div>
              </div>
              <p>{resource.description}</p>
            </div>
            <button className="high-emphasis mt-6" onClick={handleOpen}>
              LEAVE A REVIEW
            </button>
          </div>
        </div>
        <div className="grid gap-6 mt-6 xl:mt-10 md:mx-auto sm:mx-auto">
          {reviews.map((review, index) => (
            <Review
              url={review.imageUrl}
              date={review.date}
              username={review.username}
              rating={review.rating}
              content={review.content}
              key={index}
            />
          ))}
        </div>
      </div>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          Leave A Review
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[600],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="grid gap-[12px] mt-[12px] w-[480px] sm:w-full">
            <Rating
              value={reviewRating}
              onChange={(e: any, value: any) => {
                setReviewRating(value);
              }}
            />
            <TextField
              autoFocus
              margin="dense"
              type="text"
              id="Review"
              multiline
              rows={5}
              label="Review"
              fullWidth
              value={reviewContent}
              onChange={(e: any) => {
                setReviewContent(e.target.value);
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Submit</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default ResourceView;
